'use client';

import { useCallback, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { TippyTooltip } from '@nx-frontend/shared/ui/tooltip';
import { useDebounce } from '@nx-frontend/shared/hooks';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import {
  $transcriptPanel,
  removeTranscriptItemEvent,
  editSpeakerEvent,
  editDescriptionEvent,
} from '@nx-frontend/shared/store/transcript-panel';
import { TranscriptSearch } from './transcript-search';
import { TranscriptSpeakerCard } from './transcript-speaker-card';
import { scrollToTranscriptCard } from './utils/scroll-to-transcript-card';

const colors = ['sky', 'green', 'violet', 'yellow', 'purple', 'indigo', 'red', 'blue', 'orange', 'pink'];

const formatTranscriptTime = (time: string): string => {
  const withoutMs = time.split('.')[0];
  return withoutMs;
};

const selectSubstr = (substring: string, str: string) => {
  const subLowerCase = substring.toLowerCase();
  const regex = new RegExp(`(${subLowerCase})`, 'gi');
  return str.replace(regex, (match, p1) => {
    if (match.toLowerCase() === subLowerCase) {
      return `<span class="bg-green-100 font-bold px-[3px] py-[4px] rounded-sm">${p1}</span>`;
    }
    return match;
  });
};

export function TranscriptSpeakers() {
  const [transcriptPanel, removeTranscriptItem, currentWorkspace, editSpeaker, editDescription] = useUnit([
    $transcriptPanel,
    removeTranscriptItemEvent,
    $currentWorkspace,
    editSpeakerEvent,
    editDescriptionEvent,
  ]);
  const { toast } = useToast();
  const [searchValue, setSearchValue] = useState('');
  const [resultIndexs, setResultIndexs] = useState<number[]>([]);

  const t = useTranslations('features');

  const cards = useMemo(() => {
    const speakerIcon: Record<string, { color: string; text?: number }> = {};
    const { attachment, entities } = transcriptPanel;
    const annotations = entities[attachment.globalId]?.annotations || [];

    let colorIndex = 0;

    annotations.forEach((annotation) => {
      if (!speakerIcon[annotation.stt.speaker]) {
        speakerIcon[annotation.stt.speaker] = {
          color: colors[colorIndex],
          text: ['sky'].includes(colors[colorIndex]) ? 300 : 400,
        };
        colorIndex++;
        if (colorIndex > colors.length - 1) {
          colorIndex = 0;
        }
      }
    });

    return annotations.map((annotation, index) => ({
      index,
      speaker: annotation.stt.speaker,
      time: formatTranscriptTime(annotation.stt.start),
      description: annotation.description,
      descriptionHtml: annotation.description,
      icon: speakerIcon[annotation.stt.speaker],
    }));
  }, [transcriptPanel]);

  const formattedText = useMemo(() => {
    const { attachment, entities } = transcriptPanel;
    const annotations = entities[attachment.globalId]?.annotations || [];
    return annotations.map((item) => `${item.stt.speaker}: ${item.description}`).join('\n');
  }, [transcriptPanel]);

  const copyToBuffer = async () => {
    if (navigator.clipboard) {
      try {
        if (formattedText) {
          await navigator.clipboard.writeText(formattedText);
          toast({ title: t('transcript.copied') });
        }
      } catch (err) {
        console.info('Failed to copy: ', err);
        toast({ title: t('transcript.failed_to_copy'), description: t('transcript.clipboard_not_available') });
      }
    } else {
      toast({ title: t('transcript.failed_to_copy'), description: t('transcript.clipboard_not_available') });
    }
  };

  const downloadAsTextFile = () => {
    try {
      if (formattedText) {
        const blob = new Blob([formattedText], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = 'transcript.txt';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.info('Failed to download: ', err);
      toast({ title: t('transcript.failed_to_download'), description: t('transcript.unknown_error') });
    }
  };

  const attachmentId = useMemo(() => {
    const { attachment } = transcriptPanel;
    return attachment.globalId;
  }, [transcriptPanel]);

  const handleRemoveCard = (index: number) => {
    removeTranscriptItem({
      attachmentId,
      index,
      workspaceId: currentWorkspace.globalId,
    });
  };

  const handleEditSpeaker = (oldSpeaker: string, newSpeaker: string) => {
    editSpeaker({
      attachmentId,
      workspaceId: currentWorkspace.globalId,
      oldSpeaker,
      newSpeaker,
    });
  };
  const handleEditText = (index: number, newValue: string) => {
    editDescription({
      attachmentId,
      workspaceId: currentWorkspace.globalId,
      index,
      newValue,
    });
  };
  const preparedCards = useMemo(() => {
    if (!searchValue) {
      setResultIndexs([]);
      return cards;
    }

    const resultIndexs: number[] = [];
    const regExp = new RegExp(searchValue, 'gi');

    cards.forEach((card, index) => {
      if (card.description.match(regExp)) {
        resultIndexs.push(index);
      }
    });

    setResultIndexs(resultIndexs);

    if (resultIndexs.length) {
      scrollToTranscriptCard(resultIndexs[0]);
    }

    return cards.map((card) => ({
      ...card,
      descriptionHtml: selectSubstr(searchValue, card.description),
    }));
  }, [searchValue, cards, setResultIndexs]);

  const handleClearSearchInput = useCallback(() => {
    setSearchValue('');
  }, []);

  const debouncedSearchValue = useDebounce(setSearchValue, 500);

  const handleSearchInput = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value;
      if (value === '') {
        handleClearSearchInput();
      }
      debouncedSearchValue(e.currentTarget.value);
    },
    [debouncedSearchValue, handleClearSearchInput]
  );

  return (
    <>
      <div className="bg-purple-50 text-purple-400"></div>
      <div className="rounded-t-[8px] bg-cyan-900 h-[42px] flex items-center justify-end px-[15px]">
        <div className="flex">
          <TippyTooltip position="top" interactive={false} show={true} message={t('transcript.copy')}>
            <button onClick={copyToBuffer}>
              <Icon icon={IconsMap.COPY} className="w-[16px] h-[16px] text-white" />
            </button>
          </TippyTooltip>
          <TippyTooltip position="top" interactive={false} show={true} message={t('transcript.download')}>
            <button onClick={downloadAsTextFile}>
              <Icon icon={IconsMap.DOWNLOAD_2} className="w-[16px] h-[16px] text-white ml-[10px]" />
            </button>
          </TippyTooltip>
        </div>
      </div>
      <div className="grow flex flex-col pl-[16px] pr-[8px] py-[8px]">
        <TranscriptSearch
          resultIndexs={resultIndexs}
          handleSearchInput={handleSearchInput}
          handleClear={handleClearSearchInput}
          showArrows={!!searchValue}
        />

        <div data-ref="transcript-cards" className="grow overflow-y-auto h-1 pr-[8px]">
          {preparedCards.map((card, index) => (
            <TranscriptSpeakerCard
              key={index}
              {...card}
              onRemove={() => handleRemoveCard(index)}
              onEditSpeaker={(oldValue, newValue) => {
                handleEditSpeaker(oldValue, newValue);
              }}
              onEditText={(index, newValue) => {
                handleEditText(index, newValue);
              }}
            />
          ))}
        </div>
      </div>
    </>
  );
}
